import { formatCurrency } from "@/lib/currency";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import { formatAddressShort } from "@/lib/addressFormat";
import { resolveCollectionAddress, resolveCollectionPostcode } from "@/lib/jobAddressHelpers";
import { format } from "date-fns";
import { Edit, XCircle, Trash2, User, ArrowLeftRight, Building2, Calendar } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";

interface Driver {
  id: string;
  name: string;
}

interface DesktopJobsTableProps {
  jobs: any[];
  drivers: Driver[];
  isAdmin: boolean;
  variant: "pending" | "awaiting_delivery" | "completed";
  subNames?: Record<string, string>;
  subDriverNames?: Record<string, string>;
  onAssignDriver?: (jobId: string, driverId: string) => void;
  onEdit?: (jobId: string) => void;
  onCancel?: (job: any) => void;
  onDelete?: (jobId: string, e: React.MouseEvent) => void;
  onReschedule?: (jobId: string, date: string, time: string | null) => void;
}

export function DesktopJobsTable({
  jobs,
  drivers,
  isAdmin,
  variant,
  subNames = {},
  subDriverNames = {},
  onAssignDriver,
  onEdit,
  onCancel,
  onDelete,
  onReschedule,
}: DesktopJobsTableProps) {
  const navigate = useNavigate();
  const [editingSchedule, setEditingSchedule] = useState<string | null>(null);

  const safeFormat = (dateStr: string | null | undefined, fmt: string) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return format(d, fmt);
    } catch {
      return "—";
    }
  };

  const getDriverDisplay = (job: any) => {
    if (job.drivers?.name) return job.drivers.name;
    if (job.execution_type === "subcontractor" && job.driver_id && subDriverNames[job.driver_id]) {
      return subDriverNames[job.driver_id];
    }
    return null;
  };

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table className="min-w-[1100px]">
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="font-semibold whitespace-nowrap">Registration</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Vehicle</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Customer</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Collection</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Delivery</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Driver</TableHead>
            <TableHead className="font-semibold whitespace-nowrap">
              {variant === "pending" ? "Scheduled" : variant === "completed" ? "Delivered" : "Scheduled"}
            </TableHead>
            <TableHead className="font-semibold whitespace-nowrap">Status</TableHead>
            {isAdmin && <TableHead className="text-right font-semibold whitespace-nowrap">Cost</TableHead>}
            {isAdmin && (onEdit || onCancel || onDelete) && <TableHead className="text-right font-semibold whitespace-nowrap">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => {
            const driverName = getDriverDisplay(job);
            // SINGLE OWNER: resolveCollectionAddress (jobAddressHelpers.ts)
            const collectAddr = resolveCollectionAddress(job);
            const collectPostcode = resolveCollectionPostcode(job);

            return (
              <TableRow
                key={job.id}
                className="cursor-pointer hover:bg-accent/5 transition-colors"
                onClick={() => {
                  // Route to the page that can actually show this job:
                  // drafts → editor, finished jobs → Job Workspace, live jobs → workflow.
                  if (job.is_draft) {
                    navigate(`/create-job?jobId=${job.id}`);
                  } else if (variant === "completed" || job.completed_at || job.cancelled_at || job.status === "completed" || job.status === "cancelled") {
                    navigate(`/job/${job.id}`);
                  } else if (variant === "pending") {
                    navigate(`/create-job?jobId=${job.id}`);
                  } else {
                    navigate(`/resume-job/${job.id}`);
                  }
                }}

              >
                {/* Registration */}
                <TableCell className="py-2.5 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    {(!job.vehicle_registration || job.vehicle_registration === 'TBD' || job.vehicle_registration === 'N/A') ? (
                      <>
                        <span className="text-xs text-muted-foreground">N/A</span>
                        {job.vin_number && (
                          <span className="text-[10px] text-muted-foreground font-mono">
                            VIN: {job.vin_number}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm font-semibold tracking-wide text-foreground">
                        {job.vehicle_registration}
                      </span>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {job.is_return_leg && (
                        <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 text-[9px] px-1 py-0">
                          <ArrowLeftRight className="w-2.5 h-2.5 mr-0.5" />RETURN
                        </Badge>
                      )}
                      {job.execution_type === 'subcontracted' && (
                        <Badge variant="outline" className="border-primary/50 text-primary text-[9px] px-1 py-0">
                          <Building2 className="w-2.5 h-2.5 mr-0.5" />
                          {job.executing_company_id && subNames[job.executing_company_id] ? subNames[job.executing_company_id] : 'Sub'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>

                {/* Vehicle */}
                <TableCell className="py-2.5 whitespace-nowrap">
                  <span className="text-sm font-medium">
                    {job.vehicle_make} {job.vehicle_model}
                  </span>
                </TableCell>

                {/* Customer */}
                <TableCell className="py-2.5 whitespace-nowrap">
                  <span className="text-sm">{job.customers?.name || "—"}</span>
                </TableCell>

                {/* Collection */}
                <TableCell className="py-2.5 whitespace-nowrap">
                  <span className="text-xs text-muted-foreground">
                    {formatAddressShort(collectAddr, collectPostcode) || "—"}
                  </span>
                </TableCell>

                {/* Delivery */}
                <TableCell className="py-2.5 whitespace-nowrap">
                  <span className="text-xs text-muted-foreground">
                    {formatAddressShort(job.delivery_address, job.delivery_postcode) || "—"}
                  </span>
                </TableCell>

                {/* Driver */}
                <TableCell className="py-2.5" onClick={(e) => e.stopPropagation()}>
                  {isAdmin && onAssignDriver ? (
                    <Select
                      value={job.driver_id || ""}
                      onValueChange={(value) => onAssignDriver(job.id, value)}
                    >
                      <SelectTrigger className="h-8 text-xs w-[140px] bg-background">
                        <SelectValue placeholder="Assign driver..." />
                      </SelectTrigger>
                      <SelectContent className="z-[200]">
                        {drivers.map((driver) => (
                          <SelectItem key={driver.id} value={driver.id}>
                            {driver.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : driverName ? (
                    <Badge variant="secondary" className="text-xs font-normal">
                      {driverName}
                    </Badge>
                  ) : (
                    <span className="text-xs text-destructive font-medium">Unassigned</span>
                  )}
                </TableCell>

                {/* Date */}
                <TableCell className="py-2.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {(variant === "pending" || variant === "awaiting_delivery") && isAdmin && onReschedule ? (
                    <ScheduleInlineEdit
                      jobId={job.id}
                      scheduledDate={job.scheduled_date}
                      scheduledTime={job.scheduled_time}
                      isEditing={editingSchedule === job.id}
                      onStartEdit={() => setEditingSchedule(job.id)}
                      onSave={(date, time) => {
                        onReschedule(job.id, date, time);
                        setEditingSchedule(null);
                      }}
                      onCancel={() => setEditingSchedule(null)}
                    />
                  ) : variant === "pending" || variant === "awaiting_delivery" ? (
                    <span className="text-xs text-muted-foreground">
                      {job.scheduled_date
                        ? safeFormat(job.scheduled_date, "dd MMM yyyy")
                        : "—"}
                      {job.scheduled_date && job.scheduled_time ? (
                        <span className="block text-[10px] opacity-70">{job.scheduled_time}</span>
                      ) : !job.scheduled_date ? (
                        <span className="block text-[10px] opacity-70">—</span>
                      ) : null}
                    </span>
                  ) : variant === "completed" ? (
                    <span className="text-xs text-muted-foreground">
                      {job.delivery_completed_at
                        ? safeFormat(job.delivery_completed_at, "dd MMM yyyy")
                        : job.completed_at
                        ? safeFormat(job.completed_at, "dd MMM yyyy")
                        : "—"}
                      {(job.delivery_completed_at || job.completed_at) && (
                        <span className="block text-[10px] opacity-70">
                          {safeFormat(job.delivery_completed_at || job.completed_at, "HH:mm")}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {job.collection_completed_at
                        ? safeFormat(job.collection_completed_at, "dd MMM yyyy")
                        : job.collection_skipped
                        ? "Skipped"
                        : "—"}
                      {job.collection_completed_at && (
                        <span className="block text-[10px] opacity-70">
                          {safeFormat(job.collection_completed_at, "HH:mm")}
                        </span>
                      )}
                    </span>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell className="py-2.5">
                  <JobStatusBadge
                    status={variant === "awaiting_delivery" ? "in_progress" : job.status}
                    isDraft={job.is_draft}
                    collectionCompleted={variant === "awaiting_delivery" || variant === "completed" ? true : !!job.vehicle_appraisals?.completed_at}
                    collectionSkipped={job.collection_skipped || false}
                    deliveryCompleted={variant === "completed" ? true : undefined}
                    cancelledAt={job.cancelled_at}
                    cancelReason={job.cancel_reason}
                    size="sm"
                  />
                </TableCell>

                {/* Cost */}
                {isAdmin && (
                  <TableCell className="py-2.5 text-right whitespace-nowrap">
                    {(job.invoice_amount ?? job.estimated_cost) ? (
                      <span className="text-sm font-bold">
                        {formatCurrency(((job.invoice_amount ?? job.estimated_cost) || 0).toFixed(2))}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}

                {/* Actions */}
                {isAdmin && (onEdit || onCancel || onDelete) && (
                  <TableCell className="py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => onEdit(job.id)}
                          title="Edit"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onCancel && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => onCancel(job)}
                          title="Cancel"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => onDelete(job.id, e)}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// Inline schedule editor component
function ScheduleInlineEdit({
  jobId,
  scheduledDate,
  scheduledTime,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
}: {
  jobId: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (date: string, time: string | null) => void;
  onCancel: () => void;
}) {
  const safeParseDate = (str: string | null): Date | undefined => {
    if (!str) return undefined;
    try {
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(str)
        ? `${str}T00:00:00`
        : str;
      const d = new Date(normalized);
      return Number.isNaN(d.getTime()) ? undefined : d;
    } catch {
      return undefined;
    }
  };

  const safeFmt = (str: string | null, fmt: string, fallback = "Not set") => {
    const d = safeParseDate(str);
    if (!d) return fallback;
    try { return format(d, fmt); } catch { return fallback; }
  };

  const [date, setDate] = useState<Date | undefined>(safeParseDate(scheduledDate));
  const [time, setTime] = useState(scheduledTime || "");

  useEffect(() => {
    if (isEditing) {
      setDate(safeParseDate(scheduledDate));
      setTime(scheduledTime || "");
    }
  }, [isEditing, scheduledDate, scheduledTime]);

  if (!isEditing) {
    return (
      <button
        onClick={onStartEdit}
        className="text-left group/sched hover:bg-accent/50 rounded px-1.5 py-1 -mx-1.5 -my-1 transition-colors"
        title="Click to change schedule"
      >
        <span className="text-xs text-muted-foreground">
          {safeFmt(scheduledDate, "dd MMM yyyy")}
        </span>
        {scheduledTime && (
          <span className="block text-[10px] text-muted-foreground opacity-70">{scheduledTime}</span>
        )}
        <span className="block text-[10px] text-primary opacity-0 group-hover/sched:opacity-100 transition-opacity">
          Click to edit
        </span>
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[160px]">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs justify-start w-full">
            <Calendar className="w-3 h-3 mr-1.5 flex-shrink-0" />
            {date ? format(date, "dd MMM yyyy") : "Pick date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[300]" align="start">
          <CalendarComponent
            mode="single"
            selected={date}
            onSelect={(selectedDate) => {
              setDate(selectedDate);
              if (selectedDate) {
                const dateStr = format(selectedDate, "yyyy-MM-dd");
                onSave(dateStr, time || null);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        value={time}
        onChange={(e) => setTime(e.target.value)}
        className="h-7 text-xs w-full"
      />
      <div className="flex gap-1">
        <Button
          size="sm"
          className="h-6 text-[10px] px-2 flex-1"
          disabled={!date}
          onClick={() => {
            if (date) {
              const dateStr = format(date, "yyyy-MM-dd");
              onSave(dateStr, time || null);
            }
          }}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[10px] px-2"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
